/**
 * Tools for whoever runs this deployment, as opposed to whoever runs a pool.
 *
 * Renders nothing at all unless the server says the signed-in user owns the
 * deployment. That check is for tidiness, not security: hiding a button stops
 * nobody from calling the endpoint, so every action here is refused server
 * side as well. This only keeps the control out of the way of the people it
 * would do no good for.
 */

import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface SyncOutcome {
  league: string | null;
  teams: number;
  error: string | null;
  moreDue: boolean;
  logosMirrored: number;
  logosRemaining: number | null;
}

export function OwnerTools() {
  const [isOwner, setIsOwner] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SyncOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // A failure here means no tools, which is the right way to be wrong.
    api
      .me()
      .then((res) => setIsOwner(res.isOwner))
      .catch(() => setIsOwner(false));
  }, []);

  if (!isOwner) return null;

  const sync = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.syncTeams());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card owner-tools">
      <button
        type="button"
        className="owner-tools-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>Owner tools</span>
        <span className="small muted">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className="stack" style={{ gap: 10, marginTop: 12 }}>
          <div>
            <strong style={{ display: 'block' }}>Team library</strong>
            <span className="small muted">
              Rebuilds whichever league is most overdue, one per press. This
              runs hourly on its own; use this only when waiting for the hour
              is not wanted.
            </span>
          </div>

          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn-primary" disabled={busy} onClick={() => void sync()}>
              {busy ? 'Syncing\u2026' : 'Sync teams now'}
            </button>
          </div>

          {error && (
            <p className="small" role="alert" style={{ color: 'var(--danger)' }}>
              {error}
            </p>
          )}

          {result && (
            <div className="small muted">
              {result.error ? (
                <div style={{ color: 'var(--danger)' }}>
                  {result.league} failed: {result.error}
                </div>
              ) : result.league ? (
                <div>
                  {result.league}: {result.teams} teams
                </div>
              ) : result.logosMirrored > 0 ? (
                <div>
                  {result.logosMirrored} logos mirrored
                  {result.logosRemaining ? `, ${result.logosRemaining} to go` : ''}
                </div>
              ) : (
                <div>Everything is up to date.</div>
              )}
              {result.moreDue && <div>Another league is due — press again.</div>}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
